/* eslint-disable max-len */
import { useEffect, useMemo, useState } from "react";
import { type EChartsOption } from "echarts";
import { numFormatter } from "../../../helpers/utils";
import BaseChart from "../../../components/baseChart";
import type { ComparisonWidgetData } from "../types";

const BASE_COLOR = "#2DB6F5";
const COMPARE_COLOR = "#FF8A65";
const LINE_BASE_COLOR = "#0D8A6A";
const LINE_COMPARE_COLOR = "#E289F2";

interface ComparisonChartProps {
  widgetData: ComparisonWidgetData;
  width: number;
}

const buildTooltip = (params: any[], barFmt: string, lineFmt: string) => {
  if (!params?.length) return "";
  const cat = params[0].axisValue;
  let html = `<div style="padding:4px 8px;min-width:180px">
    <div style="font-weight:600;margin-bottom:6px;font-size:13px">${cat}</div>`;
  for (const p of params) {
    const fmt = p.seriesType === "line" ? lineFmt : barFmt;
    html += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;font-size:12px">
      <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
      <span style="flex:1">${p.seriesName}</span>
      <span style="font-weight:600">${formatNumber(p.value ?? 0)}${fmt ? ` ${fmt}` : ""}</span>
    </div>`;
  }
  const bars = params.filter((p) => p.seriesType === "bar");
  if (bars.length === 2 && bars[1].value !== 0) {
    const pct = ((bars[0].value - bars[1].value) / Math.abs(bars[1].value)) * 100;
    const sign = pct > 0 ? "+" : "";
    const col = pct > 0 ? "#0d8a6a" : pct < 0 ? "#c62828" : "#666";
    html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #eee;font-size:12px;color:${col};font-weight:600">${sign}${pct.toFixed(1)}% change</div>`;
  }
  return html + "</div>";
};

const ComparisonChart = ({ widgetData, width }: ComparisonChartProps) => {
  const [option, setOption] = useState<EChartsOption>({});
  const barConfig = widgetData?.config?.bar;
  const lineConfig = widgetData?.config?.line;

  const chartHeight = useMemo(() => {
    if (width < 500) return 350;
    if (width < 800) return 450;
    return 500;
  }, [width]);

  useEffect(() => {
    if (!widgetData?.data?.series?.length) return;

    const series = widgetData.data.series;
    const midpoint = Math.ceil(series.length / 2);
    const baseSeries = series.slice(0, midpoint);
    const compareSeries = series.slice(midpoint);
    const xLabels = baseSeries.map((d) => d.category);
    const barFmt = barConfig?.value_formatter || "";
    const lineFmt = lineConfig?.value_formatter || "";

    const baseName = baseSeries[0]?.category || "Base";
    const compareName = compareSeries[0]?.category || "Compare";

    const echartsSeries: any[] = [
      {
        name: `${barConfig?.name || "Value"} (${baseName})`,
        type: "bar",
        barGap: "10%",
        itemStyle: { color: BASE_COLOR, borderRadius: [4, 4, 0, 0] },
        data: baseSeries.map((d) => d.bar_value),
        emphasis: { itemStyle: { opacity: 0.85 } }
      },
      {
        name: `${barConfig?.name || "Value"} (${compareName})`,
        type: "bar",
        itemStyle: { color: COMPARE_COLOR, borderRadius: [4, 4, 0, 0] },
        data: compareSeries.map((d) => d.bar_value),
        emphasis: { itemStyle: { opacity: 0.85 } }
      }
    ];

    const yAxes: any[] = [
      {
        type: "value",
        name: barConfig?.name || "",
        nameTextStyle: { fontSize: 12 },
        axisLabel: {
          fontSize: 11,
          formatter: (v: number) => `${numFormatter(v)}${barFmt ? ` ${barFmt}` : ""}`
        },
        splitLine: { lineStyle: { type: "dashed", color: "#e8e8e8" } },
        axisLine: { show: false },
        axisTick: { show: false }
      }
    ];

    if (lineConfig?.name) {
      yAxes.push({
        type: "value",
        name: lineConfig.name,
        nameTextStyle: { fontSize: 12 },
        axisLabel: {
          fontSize: 11,
          formatter: (v: number) => `${numFormatter(v)}${lineFmt ? ` ${lineFmt}` : ""}`
        },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false }
      });

      echartsSeries.push(
        {
          name: `${lineConfig.name} (${baseName})`,
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: { width: 2.5, color: LINE_BASE_COLOR },
          itemStyle: { color: LINE_BASE_COLOR },
          data: baseSeries.map((d) => d.line_value)
        },
        {
          name: `${lineConfig.name} (${compareName})`,
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: { width: 2.5, type: "dashed", color: LINE_COMPARE_COLOR },
          itemStyle: { color: LINE_COMPARE_COLOR },
          data: compareSeries.map((d) => d.line_value)
        }
      );
    }

    setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow", shadowStyle: { color: "rgba(0,0,0,0.03)" } },
        formatter: (p: any) => buildTooltip(p, barFmt, lineFmt),
        backgroundColor: "#fff",
        borderColor: "#e8e8e8",
        borderWidth: 1,
        textStyle: { color: "#333" },
        extraCssText: "border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1)"
      },
      legend: {
        type: "scroll",
        bottom: 0,
        left: "center",
        itemGap: 16,
        icon: "circle",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { fontSize: 11 },
        pageIconSize: 12,
        pageTextStyle: { fontSize: 11 }
      },
      grid: {
        left: width < 650 ? "15%" : "8%",
        right: lineConfig?.name ? (width < 650 ? "15%" : "8%") : "4%",
        top: 40,
        bottom: lineConfig?.name ? 80 : 55,
        containLabel: false
      },
      xAxis: [
        {
          type: "category",
          data: xLabels,
          axisLine: { lineStyle: { color: "#e8e8e8" } },
          axisTick: { show: false },
          axisLabel: { fontSize: 11, color: "#666" }
        }
      ],
      yAxis: yAxes,
      series: echartsSeries,
      animationDuration: 600,
      animationEasing: "cubicOut"
    });
  }, [widgetData, width]);

  return (
    <BaseChart
      height={chartHeight}
      width={width}
      options={option}
      chartType="bar"
      onClick={() => {}}
    />
  );
};

export default ComparisonChart;
